import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { UserPlus, Trash2, Users, CheckCircle2, IndianRupee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { accompanyingApi, paymentApi, readError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const money = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n || 0);

const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;
const emptyDraft = { name: "", age: "", gender: "", relation: "" };

const AccompanyingPersons = () => {
  const { user, refreshUser } = useAuth();
  const [items, setItems] = useState([]);
  const [unitAmount, setUnitAmount] = useState(1770);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [draft, setDraft] = useState(emptyDraft);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState(false);

  const isEligible =
    user?.packageId === "delegate-api" || user?.packageId === "delegate-nonapi";

  const load = async () => {
    setLoading(true);
    try {
      const res = await accompanyingApi.list();
      setItems(res.items);
      setUnitAmount(res.unitAmount);
      setPendingCount(res.pendingCount);
      setPendingTotal(res.pendingTotal);
    } catch (err) {
      toast.error(readError(err) || "Could not load accompanying persons.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEligible) load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEligible]);

  if (!isEligible) return null;

  const addOne = async (e) => {
    e?.preventDefault?.();
    if (!draft.name.trim()) return toast.error("Please enter the companion's name.");
    setBusy(true);
    try {
      await accompanyingApi.create(draft);
      setDraft(emptyDraft);
      await load();
      toast.success("Accompanying person added. Complete payment to confirm.");
    } catch (err) {
      toast.error(readError(err) || "Could not add companion.");
    } finally {
      setBusy(false);
    }
  };

  const removeOne = async (id) => {
    if (!confirm("Remove this accompanying person?")) return;
    try {
      await accompanyingApi.remove(id);
      await load();
      toast.success("Accompanying person removed.");
    } catch (err) {
      toast.error(readError(err) || "Could not remove companion.");
    }
  };

  const payAll = async () => {
    if (!RAZORPAY_KEY_ID) {
      toast.error("Razorpay is not configured on this deployment.");
      return;
    }
    if (typeof window.Razorpay !== "function") {
      toast.error("Razorpay checkout script hasn't loaded. Reload the page.");
      return;
    }
    setPaying(true);
    try {
      const order = await accompanyingApi.createRazorpayOrder();
      const rz = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "APICON 2026",
        description: `Accompanying × ${order.count}`,
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
          contact: user?.phone || "",
        },
        theme: { color: "#0B3D7A" },
        handler: async (resp) => {
          try {
            await accompanyingApi.verifyRazorpay({
              razorpayOrderId: resp.razorpay_order_id,
              razorpayPaymentId: resp.razorpay_payment_id,
              razorpaySignature: resp.razorpay_signature,
            });
            toast.success("Payment confirmed — accompanying persons are now registered.");
            await load();
            refreshUser?.();
          } catch (err) {
            toast.error(readError(err) || "Payment verification failed.");
          } finally {
            setPaying(false);
          }
        },
        modal: {
          ondismiss: () => setPaying(false),
        },
      });
      rz.open();
    } catch (err) {
      setPaying(false);
      toast.error(readError(err) || "Could not start payment.");
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="dashboard-accompanying-section"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Badge variant="outline" className="rounded-full border-apicon-navy/20 bg-apicon-mist text-apicon-navy">
            <Users className="mr-1.5 h-3 w-3" /> Accompanying persons
          </Badge>
          <h2 className="mt-2 font-display text-xl font-bold text-apicon-navy">
            Add family or non-medical guests
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            {money(unitAmount)} per companion · includes conference kit, meals & cultural evenings.
          </p>
        </div>
        {pendingCount > 0 && (
          <div className="rounded-2xl border border-apicon-gold/40 bg-apicon-gold/10 px-4 py-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-apicon-navy/70">
              {pendingCount} pending · to pay
            </div>
            <div className="mt-1 flex items-center gap-1 font-display text-2xl font-bold text-apicon-navy">
              <IndianRupee className="h-5 w-5" />
              {money(pendingTotal).replace("₹", "")}
            </div>
            <Button
              size="sm"
              onClick={payAll}
              disabled={paying}
              className="mt-2 h-9 w-full rounded-full bg-apicon-navy text-xs font-semibold text-white hover:bg-apicon-navy/90"
              data-testid="accompanying-pay-all"
            >
              {paying ? "Opening…" : `Pay for ${pendingCount}`}
            </Button>
          </div>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="mt-6 h-24 animate-pulse rounded-2xl bg-apicon-mist/60" />
      ) : items.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-apicon-navy/20 bg-apicon-mist/40 p-6 text-center text-sm text-gray-600">
          No accompanying persons added yet. Use the form below to add one or more guests.
        </div>
      ) : (
        <ul className="mt-6 space-y-2" data-testid="accompanying-list">
          {items.map((a) => (
            <li
              key={a.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white p-4"
              data-testid={`accompanying-item-${a.id}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-apicon-navy">{a.name}</span>
                  {a.status === "paid" ? (
                    <Badge className="rounded-full bg-apicon-green/15 text-apicon-green">
                      <CheckCircle2 className="mr-1 h-3 w-3" /> Paid
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="rounded-full border-apicon-gold/50 text-apicon-navy">
                      Pending payment
                    </Badge>
                  )}
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  {[a.age && `${a.age} yrs`, a.gender, a.relation].filter(Boolean).join(" · ") || "—"}
                </div>
              </div>
              {a.status === "pending" && (
                <button
                  type="button"
                  onClick={() => removeOne(a.id)}
                  className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 text-xs text-gray-500 hover:border-red-300 hover:text-red-600"
                  data-testid={`accompanying-remove-${a.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Add form */}
      <form
        onSubmit={addOne}
        className="mt-6 grid grid-cols-1 gap-3 rounded-2xl border border-gray-100 bg-apicon-mist/40 p-5 sm:grid-cols-12"
        data-testid="accompanying-add-form"
      >
        <div className="sm:col-span-5">
          <Label htmlFor="acc-name" className="text-xs font-semibold uppercase tracking-[0.12em] text-apicon-navy/70">
            Full name
          </Label>
          <Input
            id="acc-name"
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            placeholder="Companion's full name"
            className="mt-1 h-11 rounded-xl border-gray-200 bg-white"
            data-testid="accompanying-input-name"
          />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="acc-age" className="text-xs font-semibold uppercase tracking-[0.12em] text-apicon-navy/70">
            Age
          </Label>
          <Input
            id="acc-age"
            type="number"
            min={0}
            max={120}
            value={draft.age}
            onChange={(e) => setDraft((d) => ({ ...d, age: e.target.value }))}
            placeholder="—"
            className="mt-1 h-11 rounded-xl border-gray-200 bg-white"
            data-testid="accompanying-input-age"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-apicon-navy/70">Gender</Label>
          <Select
            value={draft.gender || undefined}
            onValueChange={(v) => setDraft((d) => ({ ...d, gender: v }))}
          >
            <SelectTrigger className="mt-1 h-11 rounded-xl border-gray-200 bg-white" data-testid="accompanying-input-gender">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-3">
          <Label htmlFor="acc-relation" className="text-xs font-semibold uppercase tracking-[0.12em] text-apicon-navy/70">
            Relation
          </Label>
          <Input
            id="acc-relation"
            value={draft.relation}
            onChange={(e) => setDraft((d) => ({ ...d, relation: e.target.value }))}
            placeholder="Spouse, parent, child…"
            className="mt-1 h-11 rounded-xl border-gray-200 bg-white"
            data-testid="accompanying-input-relation"
          />
        </div>
        <div className="sm:col-span-12">
          <Button
            type="submit"
            disabled={busy}
            className="h-11 rounded-full bg-apicon-navy px-6 text-sm font-semibold text-white hover:bg-apicon-navy/90"
            data-testid="accompanying-add-submit"
          >
            <UserPlus className="mr-2 h-4 w-4" />
            {busy ? "Adding…" : "Add accompanying person"}
          </Button>
          <span className="ml-3 text-xs text-gray-500">
            Add as many as you need — pay all pending in one go.
          </span>
        </div>
      </form>
    </motion.section>
  );
};

export default AccompanyingPersons;
